"use client";

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, BakeShadows } from '@react-three/drei';
// @ts-ignore: Подавление ошибки с модулем three.js (необходимо, так как @types/three определён неправильно)
import * as THREE from 'three';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js';
import MovingCarousel from '@/components/ui/movingCarousel/movCarousel';
import { 
  CharacterProvider, 
  CharacterStats, 
  CharacterSliders 
} from '@/components/models/character';

import { useNavigate } from 'react-router-dom';

// Добавляем максимальный разрешенный размер файла (5 МБ в байтах)
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 МБ


// Проверка наличия скелета и допустимого размера файла
export const validateFBXModel = async (file: File): Promise<{ valid: boolean; message: string }> => {
  // Проверка размера файла
  if (file.size > MAX_FILE_SIZE) {
    return { 
      valid: false, 
      message: `Размер файла ${(file.size / 1024 / 1024).toFixed(2)} МБ превышает максимально допустимый (5 МБ)` 
    };
  }
  
  // Чтение файла как ArrayBuffer для анализа
  const arrayBuffer = await file.arrayBuffer();
  const fbxData = new Uint8Array(arrayBuffer);
  
  // Проверка на валидность FBX файла (проверка магических чисел)
  const isBinaryFBX = fbxData.length >= 23 && 
    new TextDecoder().decode(fbxData.slice(0, 21)) === 'Kaydara FBX Binary  ';
  
  if (!isBinaryFBX && !new TextDecoder().decode(fbxData.slice(0, 5)).includes('FBX')) {
    return { valid: false, message: 'Файл не является допустимым FBX файлом' };
  }
  
  // Создаем временный элемент для загрузки и анализа файла
  const blob = new Blob([fbxData], { type: 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  
  try {
    // Загружаем модель для проверки наличия скелета
    return await new Promise((resolve) => {
      const loader = new FBXLoader();
      loader.load(
        url,
        (model: THREE.Group) => {
          let hasSkeleton = false;
          let bonesCount = 0;
          
          // Ищем кости в модели
          model.traverse((object: THREE.Object3D) => {
            if (object instanceof THREE.Bone) {
              hasSkeleton = true;
              bonesCount++;
            }
          });
          
          // Очищаем ресурсы
          URL.revokeObjectURL(url);
          
          if (!hasSkeleton) {
            resolve({ valid: false, message: 'Модель не содержит скелета' });
          } else {
            resolve({ 
              valid: true, 
              message: `Модель имеет скелет с ${bonesCount} костями` 
            });
          }
        },
        undefined,
        (error: unknown) => {
          // Очищаем ресурсы
          URL.revokeObjectURL(url);
          resolve({ valid: false, message: `Ошибка загрузки модели: ${error instanceof Error ? error.message : String(error)}` });
        }
      );
    });
  } catch (error: unknown) {
    URL.revokeObjectURL(url);
    return { 
      valid: false, 
      message: `Не удалось проанализировать модель: ${error instanceof Error ? error.message : String(error)}` 
    };
  }
};

// Компонент-контейнер для просмотра модели
const ModelViewContainer: React.FC<{
  fileId: string | null;
}> = ({ fileId }) => {
  const [snapshotUrl, setSnapshotUrl] = useState<string | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [standardModel, setStandardModel] = useState<THREE.Group | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [backgroundColor, setBackgroundColor] = useState<string>("#ccfbf1");
  const navigate = useNavigate();
  
  // Загрузка модели для стандартизированного отображения
  useEffect(() => {
    if (!fileId) return;
    
    setLoading(true);
    setError(null);
    setSnapshotUrl(null); // Сбрасываем снимок при загрузке новой модели
    
    console.log(`ModelViewContainer: Загружаем модель с ID: ${fileId}`);
    
    const loader = new FBXLoader();
    loader.load(
      `/api/model/${fileId}`,
      (loadedModel: THREE.Group) => {
        try {
          console.log('ModelViewContainer: Модель успешно загружена', loadedModel);
          
          // Применяем стандартизацию к модели
          const standardizedModel = setupModelForStandardizedView(loadedModel);
          setStandardModel(standardizedModel);
          
          // Проверяем содержимое модели для отладки
          let meshCount = 0;
          let boneCount = 0;
          
          standardizedModel.traverse((child: any) => {
            if (child instanceof THREE.Mesh) meshCount++;
            if (child instanceof THREE.Bone) boneCount++;
          });
          
          console.log(`ModelViewContainer: Модель содержит ${meshCount} мешей и ${boneCount} костей`);
          setLoading(false);
        } catch (err) {
          console.error('ModelViewContainer: Ошибка стандартизации модели', err);
          setError('Не удалось стандартизировать модель');
          setLoading(false);
        }
      },
      (xhr: { loaded: number; total: number }) => {
        // Добавляем логирование прогресса загрузки
        const percentComplete = xhr.total ? Math.round((xhr.loaded / xhr.total) * 100) : 0;
        console.log(`ModelViewContainer: Загрузка - ${percentComplete}%`);
      },
      (err: unknown) => {
        console.error('ModelViewContainer: Ошибка загрузки', err);
        setError(`Не удалось загрузить модель: ${err instanceof Error ? err.message : String(err)}`);
        setLoading(false);
      }
    );
    
    return () => {
      // Очистка при размонтировании
      if (standardModel) {
        standardModel.traverse((child: any) => {
          if (child instanceof THREE.Mesh) {
            if (child.geometry) child.geometry.dispose();
            if (child.material) {
              if (Array.isArray(child.material)) {
                child.material.forEach((mat: any) => mat.dispose());
              } else {
                child.material.dispose();
              }
            }
          }
        });
      }
    };
  }, [fileId]);
  
  // Функция настройки модели для стандартизированного вида
  function setupModelForStandardizedView(model: THREE.Group): THREE.Group {
    // Создаем новую группу, чтобы не менять оригинальную модель
    const container = new THREE.Group();
    container.add(model.clone());
    
    // Определяем общий бокс для модели
    const box = new THREE.Box3().setFromObject(container);
    const size = box.getSize(new THREE.Vector3());
    const height = size.y;
    
    // 1. Базовое выравнивание - ставим модель ногами на землю
    // Перемещаем так, чтобы нижняя точка модели была на уровне Y=0
    model.position.y = -box.min.y;
    
    // Центрируем модель по X и Z
    const center = box.getCenter(new THREE.Vector3());
    model.position.x = -center.x;
    model.position.z = -center.z;
    
    // 2. Если есть скелет, используем его для более точного выравнивания
    let hasSkeleton = false;
    let rootBone = null;
    let bonesFound = 0;
    let boneNames: string[] = [];
    
    // Поиск всех костей и корневой кости (таз/hips)
    model.traverse((object: any) => {
      if (object instanceof THREE.Bone) {
        hasSkeleton = true;
        bonesFound++;
        boneNames.push(object.name);
        
        const name = object.name.toLowerCase();
        if (name.includes('hips') || name.includes('pelvis')) {
          rootBone = object;
        }
      }
    });
    
    if (hasSkeleton && rootBone) {
      // Получаем мировую позицию корневой кости
      const rootPosition = new THREE.Vector3();
      (rootBone as THREE.Bone).getWorldPosition(rootPosition);
      
      // Центрируем модель по корневой кости (X и Z)
      model.position.x = -rootPosition.x;
      model.position.z = -rootPosition.z;
      
      // ВАЖНО: Корректируем Y-позицию так, чтобы модель стояла на земле,
      // но учитываем положение таза
      const boxAfterCentering = new THREE.Box3().setFromObject(model);
      model.position.y = -boxAfterCentering.min.y;
      
      // Поиск костей для определения направления "вперед"
      let frontBone = null;
      let backBone = null;
      
      model.traverse((object: any) => {
        if (object instanceof THREE.Bone) {
          const name = object.name.toLowerCase();
          if (name.includes('spine2') || name.includes('chest')) {
            frontBone = object;
          }
          if (name.includes('spine') && !name.includes('spine1') && !name.includes('spine2')) {
            backBone = object;
          }
        }
      });
      
      // Разворачиваем модель лицом к камере
      if (frontBone && backBone) {
        const frontPos = new THREE.Vector3();
        const backPos = new THREE.Vector3();
        (frontBone as THREE.Bone).getWorldPosition(frontPos);
        (backBone as THREE.Bone).getWorldPosition(backPos);
        
        const frontDirection = new THREE.Vector3().subVectors(frontPos, backPos).normalize();
        
        // 2.1 Поворот по оси Y - чтобы модель смотрела на камеру
        // Игнорируем вертикальную составляющую для поворота по Y
        const flatDirection = new THREE.Vector3(frontDirection.x, 0, frontDirection.z).normalize();
        const rotationAngleY = Math.atan2(flatDirection.x, flatDirection.z);
        model.rotateY(Math.PI - rotationAngleY);
        
        // 2.2 Поворот по оси X - чтобы модель не смотрела вверх или вниз
        // Вычисляем угол между горизонтальной плоскостью и направлением "вперед"
        const xzLength = Math.sqrt(frontDirection.x * frontDirection.x + frontDirection.z * frontDirection.z);
        const pitchAngle = Math.atan2(frontDirection.y, xzLength);
        
        // Применяем поворот вокруг оси X, чтобы выровнять модель вертикально
        model.rotateX(-pitchAngle);
      } else {
        // Если не нашли нужные кости, поворачиваем по умолчанию
        model.rotateY(Math.PI);
      }
    } else {
      // Поворачиваем модель лицом к камере
      model.rotateY(Math.PI);
    }
    
    // 3. Проверяем финальное положение и корректируем при необходимости
    const finalBox = new THREE.Box3().setFromObject(model);
    
    // 4. Устанавливаем подходящий масштаб (если нужно)
    const targetHeight = 2; // Удобная высота для модели
    const scale = targetHeight / height;
    model.scale.set(scale, scale, scale);
    
    // 5. Финальное центрирование по X и Z
    model.position.x = 0;
    model.position.z = 0;

    // Фиксированная ориентация - принудительно устанавливаем нулевой поворот по X
    model.rotation.x = 0; // Установка вертикального положения

    // 8. Установка ног на землю
    
    // Ищем кости ступней по различным шаблонам именования в Mixamo
    let leftFoot: any = null;
    let rightFoot: any = null;
    let footBones: {name: string, isLeft: boolean, bone: any}[] = [];
    
    model.traverse((object: any) => {
      if (object instanceof THREE.Bone) {
        const name = object.name.toLowerCase();
        
        // Проверяем различные шаблоны именования для костей ступней
        const isFootBone =
          name.includes('foot') ||
          name.includes('ankle') ||
          name.includes('toe');
        
        if (isFootBone) {
          // Определяем, левая это нога или правая
          const isLeft =
            name.includes('left') ||
            name.includes('l_') ||
            name.includes('_l') ||
            name.startsWith('l') ||
            name.endsWith('l');
          
          const isRight =
            name.includes('right') ||
            name.includes('r_') ||
            name.includes('_r') ||
            name.startsWith('r') ||
            name.endsWith('r');
          
          if (isLeft) {
            footBones.push({name: object.name, isLeft: true, bone: object});
            if (!leftFoot) leftFoot = object;
          } else if (isRight) {
            footBones.push({name: object.name, isLeft: false, bone: object});
            if (!rightFoot) rightFoot = object;
          }
        }
      }
    });
    
    // Если нашли кости ступней, устанавливаем их на землю
    if (leftFoot) {
      const footY = leftFoot.getWorldPosition(new THREE.Vector3()).y;
      if (footY < 0) {
        model.position.y -= footY;
      }
    }
    
    if (rightFoot) {
      const footY = rightFoot.getWorldPosition(new THREE.Vector3()).y;
      if (footY < 0) {
        model.position.y -= footY;
      }
    }
    
    // Если не нашли кости ступней, используем базовое выравнивание по боксу
    if (!leftFoot && !rightFoot) {
      const finalBox = new THREE.Box3().setFromObject(model);
      if (finalBox.min.y < 0) {
        model.position.y -= finalBox.min.y;
      }
    }
    
    return model;
  }
  
  // Функция для создания снимка - перемещаем внутрь useCallback
  const captureSnapshot = useCallback((model: THREE.Group): Promise<string> => {
    console.log('ModelViewContainer: Начало создания снимка с задержкой');
    
    // Перемещаем функцию renderModelToImage внутрь useCallback
    function renderModelToImage(model: THREE.Group): string {
      console.log('ModelViewContainer: Рендеринг модели в изображение');
      
      // Создаем renderer
      const renderer = new THREE.WebGLRenderer({ 
        antialias: true,
        alpha: true,
        preserveDrawingBuffer: true,
        powerPreference: 'high-performance'
      });
      renderer.setSize(500, 500);
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      renderer.toneMappingExposure = 1.2;
      
      // Создаем сцену
      const scene = new THREE.Scene();
      
      // Используем выбранный пользователем цвет фона
      scene.background = new THREE.Color(backgroundColor);
      
      // Освещение
      // Основной рассеянный свет для заполнения теней
      scene.add(new THREE.AmbientLight(0xffffff, 0.5));
      
      // Добавляем полусферическое освещение для более реалистичной сцены
      const hemiLight = new THREE.HemisphereLight(0xffeeb1, 0xffffff, 0.3);
      scene.add(hemiLight);
      
      // Основной свет сверху - имитация солнца
      const topLight = new THREE.DirectionalLight(0xffffff, 1.2);
      topLight.position.set(0, 10, 3); // Поднимаем свет выше и немного вперед для лучших теней
      topLight.castShadow = true;
      topLight.shadow.mapSize.width = 2048; // Увеличиваем разрешение теней
      topLight.shadow.mapSize.height = 2048;
      topLight.shadow.camera.near = 0.5;
      topLight.shadow.camera.far = 50;
      topLight.shadow.camera.left = -5;
      topLight.shadow.camera.right = 5;
      topLight.shadow.camera.top = 5;
      topLight.shadow.camera.bottom = -5;
      topLight.shadow.bias = -0.001; // Предотвращаем артефакты теней
      topLight.shadow.radius = 4; // Более мягкие края теней
      scene.add(topLight);
      
      // Ключевой свет спереди-сбоку для выделения форм
      const keyLight = new THREE.DirectionalLight(0xfff2d6, 0.8); // Слегка теплый оттенок
      keyLight.position.set(3, 2, 3);
      keyLight.castShadow = true;
      keyLight.shadow.mapSize.width = 1024;
      keyLight.shadow.mapSize.height = 1024;
      scene.add(keyLight);
      
      // Заполняющий свет с другой стороны для подсветки теней
      const fillLight = new THREE.DirectionalLight(0xe6f5ff, 0.4); // Слегка холодный для контраста
      fillLight.position.set(-2, 1, 2);
      scene.add(fillLight);
      
      // Создаем круглый пол с отражением
      const floorMaterial = new THREE.MeshPhysicalMaterial({
        color: new THREE.Color('#eef2ff'), // Используем шестнадцатеричное значение
        metalness: 0.0,
        roughness: 0.1,
        reflectivity: 0.2,
        clearcoat: 0.5,
        clearcoatRoughness: 0.2
      });
      
      const floor = new THREE.Mesh(
        new THREE.CircleGeometry(1, 32), // Радиус 1 = диаметр 2
        floorMaterial
      );
      floor.rotation.x = -Math.PI / 2; // Поворачиваем горизонтально
      floor.position.y = -0.1; // Опускаем пол ниже, чтобы видеть ноги полностью
      floor.receiveShadow = true;
      scene.add(floor);
      
      // Клонируем модель и добавляем в сцену
      const modelClone = model.clone();
      
      // Применяем улучшения к модели
      modelClone.traverse((object: any) => {
        if (object instanceof THREE.Mesh) {
          object.castShadow = true;
          object.receiveShadow = true;
          
          // Если у объекта есть материал, улучшаем его
          if (object.material) {
            if (Array.isArray(object.material)) {
              object.material.forEach((mat: any) => {
                if (mat.type === 'MeshStandardMaterial') {
                  mat.envMapIntensity = 0.6;
                  mat.metalness = Math.min(mat.metalness || 0, 0.2); // Уменьшаем металличность
                  mat.roughness = Math.max(mat.roughness || 0.5, 0.4); // Регулируем шероховатость
                }
              });
            } else {
              if (object.material.type === 'MeshStandardMaterial') {
                object.material.envMapIntensity = 0.6;
                object.material.metalness = Math.min(object.material.metalness || 0, 0.2);
                object.material.roughness = Math.max(object.material.roughness || 0.5, 0.4);
              }
            }
          }
        }
      });
      
      scene.add(modelClone);
      
      // Настраиваем камеру
      const camera = new THREE.PerspectiveCamera(35, 1, 0.1, 100);
      camera.position.set(0, 2.1, 4);  // Увеличьте Y-координату для поднятия камеры
      camera.lookAt(0, 1, 0);
      
      // Добавляем фронтальный свет от камеры для лучшего освещения
      const frontLight = new THREE.DirectionalLight(0xffffff, 2.5);
      frontLight.position.set(0, 1.2, 3); // Позиция чуть впереди камеры
      frontLight.lookAt(0, 1, 0);
      scene.add(frontLight);
      
      // Рендерим сцену
      renderer.render(scene, camera);
      
      // Получаем данные скриншота
      const imageUrl = renderer.domElement.toDataURL('image/png');
      
      // Очищаем ресурсы
      scene.clear();
      renderer.dispose();
      
      console.log('ModelViewContainer: Изображение создано');
      return imageUrl;
    }
    
    return new Promise((resolve) => {
      setIsCapturing(true);
      // Используем setTimeout для задержки
      setTimeout(() => {
        try {
          const imageUrl = renderModelToImage(model);
          resolve(imageUrl);
        } catch (error) {
          console.error('ModelViewContainer: Ошибка при рендеринге', error);
          resolve(''); // Возвращаем пустую строку в случае ошибки
        } finally {
          setIsCapturing(false);
        }
      }, 500);
    });
  }, [backgroundColor, setIsCapturing]);
  
  // Обработчик для кнопки создания снимка
  const handleMintSFT = useCallback(async () => {
    if (standardModel && !isCapturing) {
      try {
        console.log('ModelViewContainer: Запуск создания снимка');
        const url = await captureSnapshot(standardModel);
        if (url) {
          setSnapshotUrl(url);
          console.log('ModelViewContainer: Снимок установлен в состояние');
          
          // Сохраняем данные в localStorage для доступа к ним на странице mint
          if (fileId) {
            localStorage.setItem('mintData', JSON.stringify({
              snapshotUrl: url,
              fileId
            }));
            
            // Добавляем отладочную информацию
            console.log('ModelViewContainer: Сохранили данные в localStorage и перенаправляем на /mint');
            
            // Перенаправляем на страницу чеканки SFT с абсолютным путем
            navigate('/mint', { state: { snapshotUrl: url, fileId } });
            
            // Также передаем данные через состояние навигации для дополнительной надежности
            console.log('ModelViewContainer: Перенаправление выполнено');
          }
        }
      } catch (error) {
        console.error('ModelViewContainer: Ошибка при создании снимка', error);
      }
    }
  }, [standardModel, isCapturing, fileId, navigate, captureSnapshot]);
  
  // Компонент OrbitControls с автовращением
  const ControlledOrbitControls = (props: any) => {
    const { autoRotationTimeout = 5000, ...restProps } = props;
    const controlsRef = useRef<any>(null);
    const [autoRotate, setAutoRotate] = useState(true); // Начинаем с включенного автовращения
    const lastInteraction = useRef(Date.now());
    
    // Обработчик взаимодействия пользователя
    const handleUserInteraction = useCallback(() => {
      lastInteraction.current = Date.now();
      if (autoRotate) setAutoRotate(false);
    }, [autoRotate]);
    
    // Проверяем, нужно ли включить автовращение
    useFrame(() => {
      if (!autoRotate && Date.now() - lastInteraction.current > autoRotationTimeout) {
        setAutoRotate(true);
      }
    });
    
    useEffect(() => {
      const controls = controlsRef.current;
      if (controls) {
        controls.addEventListener('start', handleUserInteraction);
        controls.addEventListener('change', handleUserInteraction);
        
        return () => {
          controls.removeEventListener('start', handleUserInteraction);
          controls.removeEventListener('change', handleUserInteraction);
        };
      }
    }, [handleUserInteraction]);
    
    return (
      <OrbitControls
        ref={controlsRef}
        autoRotate={autoRotate}
        autoRotateSpeed={1.5}
        {...restProps}
      />
    );
  };

  // Компонент для отображения стандартизированной модели
  const StandardModelView = () => {
    if (!standardModel) return null;
    console.log('ModelViewContainer: Рендеринг StandardModelView');
    return <primitive object={standardModel} />;
  };

  return (
    <CharacterProvider>
      <div className="flex flex-col md:flex-row justify-between items-center md:items-start w-full max-w-7xl mx-auto gap-6 md:gap-10 px-4 md:px-0">
        {/* Левый блок с характеристиками */}
        <CharacterStats />
        
        {/* Центральный блок с просмотром модели */}
        <div className="w-full md:w-1/3 flex justify-center">
          <div className="w-full">
            {/* Стандартизированный просмотр модели с фиксированными размерами */}
            <div className="border border-border rounded-lg overflow-hidden mb-6 bg-card">
              
              <div className="w-full h-[450px] bg-secondary relative">
                {loading ? (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                    <p className="ml-3 text-muted-foreground">Обработка модели...</p>
                  </div>
                ) : error ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-destructive bg-opacity-20">
                    <p className="text-destructive-foreground">{error}</p>
                  </div>
                ) : fileId && standardModel ? (
                  <Canvas 
                    camera={{ position: [0, 1.2, 4], fov: 35 }} 
                    shadows 
                    gl={{ antialias: true }}
                    onCreated={(state: THREE.WebGLRenderer) => {
                      console.log('ModelViewContainer: Canvas создан', state);
                    }}
                  >
                    <color attach="background" args={[backgroundColor]} />
                    
                    {/* Улучшенное освещение */}
                    <ambientLight intensity={2} />
                    <hemisphereLight args={[0xffeeb1, 0xffffff, 0.3]} />
                    
                    {/* Основной свет сверху с улучшенными настройками теней */}
                    <directionalLight 
                      position={[0, 10, 5]} 
                      intensity={1.2}
                      castShadow
                      shadow-mapSize-width={1024} 
                      shadow-mapSize-height={1024}
                      shadow-camera-left={-5}
                      shadow-camera-right={5}
                      shadow-camera-top={5}
                      shadow-camera-bottom={-5}
                      shadow-bias={-0.001}
                      shadow-radius={3}
                    />
                    
                    {/* Ключевой свет спереди-сбоку */}
                    <directionalLight 
                      position={[3, 2, 3]} 
                      intensity={2}
                      color="#d1d5db"
                      castShadow
                      shadow-mapSize-width={1024} 
                      shadow-mapSize-height={1024} 
                    />
                    
                    {/* Заполняющий свет с другой стороны */}
                    <directionalLight 
                      position={[-2, 1, 2]} 
                      intensity={0.4}
                      color="#9ca3af"
                    />
                    
                    {/* Заменяем обычные OrbitControls на модифицированные с автовращением */}
                    <ControlledOrbitControls target={[0, 1, 0]} autoRotationTimeout={10000} />
                    
                    {/* Добавляем фронтальный свет для лучшего освещения модели */}
                    <directionalLight 
                      position={[0, 1.2, 3]} 
                      intensity={1}
                      color="#e5e7eb"
                    />
                    
                    <StandardModelView />
                    
                    {/* Оптимизация теней */}
                    <BakeShadows />
                  </Canvas>
                ) : (
                  <div className="h-full flex items-center justify-center text-muted-foreground">
                    Загрузите модель для стандартизированного просмотра
                  </div>
                )}
              </div>
              
              {/* Карусель цветов */}
              {fileId && standardModel && !loading && (
                <div>
                  <MovingCarousel 
                    setBackgroundColor={setBackgroundColor} 
                    initialColor={backgroundColor} 
                  />
                  
                  {/* Кнопка Mint SFT под каруселью */}
                  <div className="flex justify-center mt-6 pb-2">
                    <button
                      onClick={handleMintSFT}
                      className="px-5 py-2.5 bg-secondary text-secondary-foreground rounded-md hover:bg-primary/90 hover:text-primary-foreground transition-colors shadow-md font-medium"
                      disabled={isCapturing}
                    >
                      {isCapturing ? 'Обработка...' : 'Mint SFT'}
                    </button>
                  </div>
                </div>
              )}
              
              {snapshotUrl && (
                <div className="p-3 border-t border-border flex justify-center">
                  <div className="border border-border rounded-md overflow-hidden" style={{ maxWidth: '200px', maxHeight: '200px' }}>
                    <img 
                      src={snapshotUrl} 
                      alt="Стандартизированный снимок" 
                      className="w-full h-auto"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Правый блок со слайдерами весов */}
        <CharacterSliders />
      </div>
    </CharacterProvider>
  );
};

export default ModelViewContainer; 